import React, { useContext, useEffect, useState } from "react";
import {
  Table,
  Space,
  Button,
  Input,
  Drawer,
  Popconfirm,
  Tag,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import { PageHeader } from "../../../../components/page-header/page-header";
import useFetch from "../../../../hooks/useFetch";
import {
  ApproveADL,
  GetCustomerList,
  GetSpecificDealerMasterData,
  SaveBulkDealerList,
} from "../../../../utils/api.constant";
import { ePrivileges, eResultCode } from "../../../../utils/enum";
import useToast from "../../../../hooks/useToast";
import { ToastType, ToastOpen } from "../../../../state/toast/slice";
import CustomDrawer from "../../../../components/drawer";
import CustomerForm from "./form";
import { TBulkUploadResponse, TCustomer } from "../../../../types/index";
import { useNavigate } from "react-router-dom";
import Utils from "../../../../utils";
import { UserContext } from "../../../../context";
import ActionCard from "../../../../components/actionCard/actionCard";
import { TFilterModel, TResponseModel } from "../../../../types/config";
import BulkUploadApprovalList from "../dealer/bulk-upload-approval-list";
import CustomModal from "../../../../components/modal";
import GenericPopover from "../../../../components/pop-over";
import {
  CalendarOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  FilterOutlined,
  IdcardOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import utils from "../../../../utils";
import ColumnManager from "../../../../components/configurable-columns";
import ExportToCSV from "../../../../components/exporttocsv/ExportToCsv";
import DateUtils from "../../../../utils/date";
import { defaultFilterParams } from "../../../../utils/constants";

interface CustomerDataType {
  key: string;
  srNo: number;
  name: string;
  code: string;
  servicePartners: string;
  pendingStatus: string;
  isActive: string;
  adlPendingForApprovalId: number[];
  hidden?: boolean;
}

export default function CustomerDataList() {
  const { post } = useFetch();
  const navigate = useNavigate();
  const { onShowToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<CustomerDataType[]>([]);
  const context: any = useContext(UserContext);
  const [bulkUploadResponse, setBulkUploadResponse] =
    useState<TBulkUploadResponse>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<TCustomer | null>();
  const [isNewADL, setNewADL] = useState<boolean>(false);
  const [isEligible, setIsEligible] = useState<boolean>(false);
  const [triggerConfirm, setTriggerConfirm] = useState<boolean>(false);
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const showModal = (user: TCustomer) => {
    setIsModalOpen(true);
    setSelectedUser(user);
  };

  useEffect(() => {
    setIsEligible(bulkUploadResponse?.error ? false : true);
  }, [bulkUploadResponse]);

  const getUploadedDealers = async (
    customer: TCustomer,
    dealerMasterId: number
  ) => {
    try {
      const requestPayload = {
        data: {
          id: dealerMasterId,
        },
      };

      const response: TResponseModel = await post(
        GetSpecificDealerMasterData,
        requestPayload
      );
      const { data, dataResponse } = response;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        const bulkUploadResp = {
          ...data?.errorCount,
          id: data?.custUploadedADLTempId,
        };
        setBulkUploadResponse(bulkUploadResp);
        showModal(customer);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
    }
  };

  const handleOk = async (customerId: number, dealerMasterId: number) => {
    try {
      setIsEligible(false);
      let requestPayload;
      let response: TResponseModel;
      if (isNewADL) {
        requestPayload = {
          data: {
            customerId,
            dealerMasterId,
            IsIDEXModifiedADLUpload: isNewADL ? true : false,
            ADLBulkUploadTempId: bulkUploadResponse?.id,
          },
        };
        response = await post(SaveBulkDealerList, requestPayload);
      } else {
        requestPayload = {
          data: {
            customerId,
            dealerMasterId,
            IsIDEXModifiedADLUpload: isNewADL ? true : false,
            ADLBulkUploadTempId: bulkUploadResponse?.id,
          },
        };
        response = await post(ApproveADL, requestPayload);
      }
      const { dataResponse } = response;
      if (
        dataResponse.returnCode === eResultCode.SUCCESS ||
        dataResponse.returnCode === eResultCode.INVALID_PASSWORD
      ) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: dataResponse.description,
        });
        setSelectedUser(null);
        setIsModalOpen(false);
        fetchData();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: dataResponse.description,
        });
        setSelectedUser(null);
      }
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
    } finally {
      setIsEligible(true);
    }
  };

  const refreshADL = (bulkUploadResponse: TBulkUploadResponse) => {
    setBulkUploadResponse(bulkUploadResponse);
    setNewADL(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const initialColumns: any[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "30%",
      filterSearch: true,
      sorter: (a: CustomerDataType, b: CustomerDataType) =>
        a.name.localeCompare(b.name),
      filters: utils.getUniqueFilters(data, "name"),
      onFilter: (value: string | number | boolean, record: CustomerDataType) =>
        record.name.includes(value as string),
    },
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      width: "10%",
      filterSearch: true,
      sorter: (a: CustomerDataType, b: CustomerDataType) =>
        a.code.localeCompare(b.code),
      filters: utils.getUniqueFilters(data, "code"),
      onFilter: (value: string | number | boolean, record: CustomerDataType) =>
        record.code.includes(value as string),
    },
    {
      title: "Service Partners",
      dataIndex: "servicePartners",
      key: "servicePartners",
      width: "30%",
      filterSearch: true,
      sorter: (a: CustomerDataType, b: CustomerDataType) =>
        a.servicePartners.localeCompare(b.servicePartners),
      filters: utils.getUniqueFilters(data, "servicePartners"),
      onFilter: (value: string | number | boolean, record: CustomerDataType) =>
        record.servicePartners.includes(value as string),
    },
    {
      title: "ADL Approval Pending",
      dataIndex: "pendingStatus",
      key: "pendingStatus",
      width: "15%",
      align: "center",
      filterSearch: true,
      sorter: (a: CustomerDataType, b: CustomerDataType) =>
        a.pendingStatus.localeCompare(b.pendingStatus),
      render: (adlPendingForApprovalId: number, record: CustomerDataType) => (
        <Tag
          color={
            record.adlPendingForApprovalId.length > 0
              ? "#f50"
              : "var(--bs-primary)"
          }
        >
          {record.adlPendingForApprovalId.length > 0 ? "Yes" : "No"}
        </Tag>
      ),
      onFilter: (value: string, record: CustomerDataType) =>
        record.pendingStatus.includes(value),
    },
    {
      title: "Action",
      key: "actions",
      align: "center",
      width: "4%",
      render: (record: TCustomer) => (
        <>
          <GenericPopover
            placement="left"
            content={
              <div className="standardActionBox">
                {/* Edit Row with CustomDrawer trigger */}

                {Utils.isUserHasAccess(
                  context.user?.privilegeList,
                  ePrivileges.EDITCUSTOMERS
                ) ? (
                  <CustomDrawer
                    trigger={
                      <ActionCard text="Edit">
                        <EditOutlined />
                      </ActionCard>
                    }
                    component={CustomerForm}
                    componentProps={{
                      id: record.id,
                      onRefreshList: () => fetchData(),
                    }}
                    title="Customer Form"
                    size="default"
                  />
                ) : null}

                {/* Dealers Row with Link */}

                {Utils.isUserHasAccess(
                  context.user?.privilegeList,
                  ePrivileges.VIEWDEALERS
                ) ? (
                  <ActionCard
                    // icon={faUsersGear}
                    text="Dealers"
                    onClick={() =>
                      navigate(
                        `/dashboard/dealers?customerId=${(record.id as number)?.toString()}&customerName=${record.name?.toString()}`
                      )
                    }
                  >
                    <UserOutlined />
                  </ActionCard>
                ) : null}

                {record?.adlPendingForApprovalId?.length &&
                Utils.isUserHasAccess(
                  context.user?.privilegeList,
                  ePrivileges.VERIFYADL
                ) ? (
                  <ActionCard
                    // icon={faCalendarCheck}
                    text="Verify ADL"
                    onClick={() =>
                      getUploadedDealers(
                        record,
                        record?.adlPendingForApprovalId?.[0] as number
                      )
                    }
                  >
                    <CalendarOutlined />
                  </ActionCard>
                ) : null}
              </div>
            }
            icon={
              <MoreOutlined style={{ fontSize: "20px", cursor: "pointer" }} />
            }
          />
        </>
      ),
    },
  ];
  const [columns, setColumns] = useState(initialColumns);
  const [draftColumns, setDraftColumns] = useState(columns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response = await post(GetCustomerList, requestPayload);
      const { dataResponse, data, filterModel } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        // Transform data to match table columns
        const formattedData = data.map((item: TCustomer, index: number) => ({
          key: (item.id ?? "").toString(),
          srNo: index + 1,
          name: item.name,
          code: item.customerCode,
          servicePartners: (item.servicePartners ?? [])
            .map((partner) => partner.name)
            .join(", "),
          pendingStatus:
            (item.adlPendingForApprovalId?.length ?? 0) > 0 ? "Yes" : "No",
          id: item.id,
          adlPendingForApprovalId: item.adlPendingForApprovalId,
        }));
        setData(formattedData);
        setFilterParams({ ...(filterModel as TFilterModel) });
        setIsLoading(false);
        updateColumnFilters(filteredData);
      } else {
        onShowToast({
          type: ToastType.error,
          title: "Error",
          position: ToastOpen.centerTop,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      onShowToast({
        type: ToastType.error,
        title: "Error",
        position: ToastOpen.centerTop,
        content: "Failed to fetch customer data",
      });
      setIsLoading(false);
    }
  };

  const updateColumnFilters = (dataset: CustomerDataType[]) => {
    const nameFilters = utils.getUniqueFilters(dataset, "name");
    const codeFilters = utils.getUniqueFilters(dataset, "code");
    const servicePartnersFilters = utils.getUniqueFilters(dataset, "servicePartners");
    const pendingStatusFilters = Array.from(
    new Set(dataset.map(item => item.pendingStatus))
  ).map(status => ({ text: status, value: status }));

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.key === "name") {
          return { ...col, filters: nameFilters };
        }
        if (col.key === "code") {
          return { ...col, filters: codeFilters };
        }
        if (col.key === "servicePartners") {
          return { ...col, filters: servicePartnersFilters };
        }
        if (col.key === "pendingStatus") {
        return { ...col, filters: pendingStatusFilters };
      }
        return col;
      })
    );
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    updateColumnFilters(filteredData);
  }, [activeFilters, data]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };


  const filteredData = data.filter((item) => {
  const searchMatch = Object.values(item).some((val) =>
      val.toString().toLowerCase().includes(searchText.toLowerCase())
    );

  const nameMatch = !activeFilters.name || 
    activeFilters.name.includes(item.name);
  
  const codeMatch = !activeFilters.code || 
    activeFilters.code.includes(item.code);
    
  const servicePartnersMatch = !activeFilters.servicePartners || 
    activeFilters.servicePartners.includes(item.servicePartners);
    
  const pendingStatusMatch = !activeFilters.pendingStatus || 
    activeFilters.pendingStatus.includes(item.pendingStatus);
  
  return searchMatch && nameMatch && codeMatch && servicePartnersMatch && pendingStatusMatch;
});

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any
  ) => {
    setActiveFilters(filters);
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      })
    );
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col
      )
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  const exportHeaders = columns
    .filter(
      (column) =>
        column.key && (column?.title as string)?.toLowerCase() !== "action"
    )
    .map((column) => ({
      label: column.title,
      key: column.key,
    }));

  const exportData = data.map((user) => ({
    ...user,
    isActive: user.isActive ? "Active" : "Inactive",
  }));

  const handleResetAll = () => {
    setActiveFilters({});
    setSearchText("");
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      }))
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };

  return (
    <div className="pageBody">
      <PageHeader
        icon={<IdcardOutlined />}
        title={`${filterParams.totalRows} Total Customers`}
        size="large"
      >
        <Space>
          {Utils.isUserHasAccess(
            context.user?.privilegeList,
            ePrivileges.ADDCUSTOMERS
          ) ? (
            <CustomDrawer
              trigger={
                <Button disabled={isLoading} type="primary">
                  Add
                </Button>
              }
              component={CustomerForm}
              componentProps={{
                id: 0,
                onRefreshList: () => {
                  navigate("/dashboard/entities");
                  fetchData();
                },
              }}
              title="Customer Form"
              size="default"
            />
          ) : null}
          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <ExportToCSV
            data={exportData}
            headers={exportHeaders}
            fileName={`customer_${DateUtils.format(new Date())}.csv`}
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
          </Tooltip>
        </Space>
      </PageHeader>

      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            // ...pagination,
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          sticky
          scroll={{
            y: "calc(100vh - 310px)", // Adjust height dynamically
          }}
        />
      </div>

      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>

      <CustomModal
        title={"ADL Upload Status"}
        useCustomHeader={true}
        isOpen={isModalOpen}
        onCancel={handleCancel}
        hideOkButton={true}
        hideCancelButton={true}
        onOk={() => {}}
        footer={[
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              paddingBottom: "2rem",
              gap: "1rem",
            }}
          >
            <Popconfirm
              key="confirm"
              title="Are you sure to perform this action?"
              visible={triggerConfirm}
              onConfirm={() =>
                handleOk(
                  selectedUser?.id as number,
                  selectedUser?.adlPendingForApprovalId?.[0] as number
                )
              }
              onCancel={() => setTriggerConfirm(false)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="primary"
                disabled={!isEligible}
                onClick={() => setTriggerConfirm(true)}
              >
                Approve
              </Button>
            </Popconfirm>
            <Button key="cancel" type="primary" onClick={handleCancel}>
              Cancel
            </Button>
          </div>,
        ]}
      >
        <BulkUploadApprovalList
          reuploadADL={true}
          customerName={selectedUser?.name}
          refreshADL={refreshADL}
          dealerMasterId={selectedUser?.adlPendingForApprovalId?.[0] as number}
          bulkUploadResponse={bulkUploadResponse}
          customerId={selectedUser?.id}
        ></BulkUploadApprovalList>
      </CustomModal>
    </div>
  );
}
